import OppCustomerCard from './oppCustomerCard';
import './OpportunityDetail.css';
import PresentPhase from './Phases/Present/PresentPhase';
import DesignPhase from './Phases/Design/DesignPhase';
import ValidatePhase from './Phases/Validate/ValidatePhase';
import TechClosePhase from './Phases/TechClose/TechClosePhase';
import OppPhaseTransitionModal from './OppPhaseTransitionModal';
import {
  postItem,
} from '../../support/queries';
import DiscoverPhase from './Phases/Discovery/DiscoveryPhase';
import OpportunityWorkHistoryList from './OpportunityWorkHistoryList';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import OppDataCard from './OppDataCard';
import OppProducts from './OppProducts';
import SEWorkLogger from './SEWorkLogger';
import { useFetchUser } from '../../hooks/user-hooks';
import { arrowLeft, arrowRight, check } from '@harbor/elements/icons';
import { useFetchCustomer } from '../../hooks/customer-hooks';
import { useFetchOpportunity, useUpdateOpportunityMutation } from '../../hooks/opportunity-hooks';
import { OpportunityPhaseLabel, OpportunityPhase } from '../../types/opportunity';
import { useModal } from '../security-toolbox-ui/StbModalContext';
import { RootState, useAppDispatch } from '../../support/store';
import { checkTaggedOpportunitiesLoaded, setNeedsRefresh } from '../../support/slices/salesforceOpportunitiesSlice';
import { useSelector } from 'react-redux';
import { CreateEventLauncher } from '../Common/MoodleEventBooking/CreateEventLauncher';
import TechnicalBlockerLogger from './TechnicalBlockerLogger';
import { updateSENote } from '../../support/salesForceHelpers';
import StbButton from '../security-toolbox-ui/StbButton';
import { HbrDivider, HbrStepper } from '@harbor/elements-react-standalone';
import StbModal from '../security-toolbox-ui/StbModal';
import NavHistoryTracker from '../../navigation/NavHistoryTracker';
import OpportunityEscalationRequestor from './Escalation/OpportunityEscalationRequestor';
import { checkApprovalTemplatesLoaded } from '../../support/slices/approvalTemplatesSlice';
import OpportunityEscalationList from './Escalation/OpportunityEscalationList';

const FEEDBACK_PHASE_GATING_ENABLED = false;

// Phases/steps constants
const phases = [
  OpportunityPhase.Discovery,
  OpportunityPhase.Present,
  OpportunityPhase.Design,
  OpportunityPhase.Validate,
  OpportunityPhase.TechClose,
];

const getStepFromPhase = (phase: OpportunityPhase) => {
  if (
    phase === OpportunityPhase.TechClose ||
    phase === OpportunityPhase.TechCloseWon ||
    phase === OpportunityPhase.TechCloseLost
  ) {
    return phases.indexOf(OpportunityPhase.TechClose);
  }

  return Math.max(phases.indexOf(phase), 0);
};

const getPhaseFromStep = (step: number) => phases[step] || OpportunityPhase.Discovery;

const maxPhase = (a?: OpportunityPhase, b?: OpportunityPhase): OpportunityPhase => {
  if (!a && !b) return OpportunityPhase.Discovery;
  if (!a) return b as OpportunityPhase;
  if (!b) return a;

  return getStepFromPhase(a) >= getStepFromPhase(b) ? a : b;
};

// Feedback triggers only for present→design, design→validate, validate→tech_close
function shouldTriggerFeedback(fromStep: number, toStep: number) {
  if (!FEEDBACK_PHASE_GATING_ENABLED) return false;

  return (
    (fromStep === 1 && toStep === 2) ||
    (fromStep === 2 && toStep === 3) ||
    (fromStep === 3 && toStep === 4)
  );
}

const OpportunityDetail = () => {
  const { id } = useParams();
  const { openModal, closeModal } = useModal();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(checkApprovalTemplatesLoaded());
    dispatch(checkTaggedOpportunitiesLoaded());
  }, [dispatch]);

  const { email } = useSelector((state: RootState) => state.user.userData);
  const { data: oppData, isLoading: isLoadingOppData } = useFetchOpportunity(id);
  const { data: user } = useFetchUser();
  const { data: customer } = useFetchCustomer(oppData?.AccountId, { enabled: !!oppData });
  const { mutateAsync: updateOpportunity } = useUpdateOpportunityMutation();

  // UI State
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState('');
  const [visibleComponent, setVisibleComponent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (!oppData?.Name) return;
    setTitle(oppData.Name);
  }, [oppData]);

  const [logAfterOppDataReload, setLogAfterOppDataReload] = useState<boolean>(false);
  const [feedback, setFeedback] = useState(null);

  // STEPPER
  const defaultSteps = useMemo(() => {
    return phases.map((phase, idx) => ({
      label: OpportunityPhaseLabel[phase],
      id: idx.toString(),
      status: '',
    }));
  }, []);

  const [currentStep, setCurrentStep] = useState(-1);
  const [highestStep, setHighestStep] = useState(-1);

  useEffect(() => {
    if (!oppData) return;

    const currentPhase =
      oppData.se_phase ||
      oppData.highest_se_phase ||
      OpportunityPhase.Discovery;

    const highestPhase =
      maxPhase(oppData.highest_se_phase, currentPhase) ||
      OpportunityPhase.Discovery;

    const highestPhaseStep = getStepFromPhase(highestPhase);

    // Select the highest reached phase by default.
    // This prevents Harbor Stepper from default-selecting Discovery first.
    setCurrentStep(highestPhaseStep);
    setHighestStep(highestPhaseStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppData?.id, oppData?.se_phase, oppData?.highest_se_phase]);

  const steps = useMemo(() => {
    return defaultSteps.map((step, idx) => ({
      ...step,
      status: idx < highestStep ? 'done' : '',
    }));
  }, [defaultSteps, highestStep]);

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setVisibleComponent(
          <DiscoverPhase />
        );
        break;
      case 1:
        setVisibleComponent(
          <PresentPhase />
        );
        break;
      case 2:
        setVisibleComponent(
          <DesignPhase />
        );
        break;
      case 3:
        setVisibleComponent(
          <ValidatePhase />
        );
        break;
      case 4:
        setVisibleComponent(
          <TechClosePhase />
        );
        break;
      default:
        setVisibleComponent(null);
    }
  }, [currentStep, id, user, oppData]);

  async function handlePhaseProgressionWithoutFeedback(newStep: number) {
    if (!oppData) return;

    const newPhase = getPhaseFromStep(newStep);

    const updatedOpportunity = {
      id: oppData.id || oppData.Id,
      se_phase: newPhase,
      highest_se_phase: maxPhase(oppData.highest_se_phase, newPhase),
      updated_by: email,
      customer_id: customer?.Id,
      init_config: true,
    };

    try {
      await updateOpportunity(updatedOpportunity);

      const phaseActivityMessages = {
        2: 'Opportunity Moved to Design phase.',
        3: 'Opportunity Moved to Validate phase.',
        4: 'Opportunity Moved to Close phase.',
      };

      if (phaseActivityMessages[newStep]) {
        await updateSENote(oppData, phaseActivityMessages[newStep]);
      }
    } catch (error) {
      console.error('Error updating opportunity phase:', error);
    }
  }

  // Navigation logic without feedback
  async function updateOpportunityPhaseWithoutFeedback(newStep: number) {
    if (!oppData) return;

    const newPhase = getPhaseFromStep(newStep);

    const updatedOpportunity = {
      id: oppData.id || oppData.Id,
      se_phase: newPhase,
      highest_se_phase: maxPhase(oppData.highest_se_phase, newPhase),
      updated_by: email,
      customer_id: customer?.Id,
      init_config: true,
    };

    try {
      await updateOpportunity(updatedOpportunity);
    } catch (error) {
      console.error('Error updating opportunity phase:', error);
    }
  }

  async function moveToStep(requestedStep: number) {
    if (!oppData || currentStep < 0) return;

    const newStep = Math.max(0, Math.min(requestedStep, phases.length - 1));

    if (newStep === currentStep) return;

    const isForwardIntoNewPhase = newStep > highestStep;

    if (isForwardIntoNewPhase) {
      dispatch(setNeedsRefresh(true));
    }

    if (
      isForwardIntoNewPhase &&
      shouldTriggerFeedback(currentStep, newStep)
    ) {
      setTransitionPhase(`${getPhaseFromStep(currentStep)}_to_${getPhaseFromStep(newStep)}`);
      setModalOpen(true);
      return;
    }

    if (isForwardIntoNewPhase) {
      await handlePhaseProgressionWithoutFeedback(newStep);
      setHighestStep((prev) => Math.max(prev, newStep));
    }

    setCurrentStep(newStep);
  }

  async function handleNext() {
    await moveToStep(currentStep + 1);
  }

  function handleBack(selected?: number) {
    const prevStep = Math.max(selected ?? currentStep - 1, 0);
    setCurrentStep(prevStep);
  }

  const handleJumpToCloseClicked = () => {
    openModal({
      content: (
        <StbModal
          open={true}
          role="save"
          submitLabel="Close Opportunity"
          onSubmit={handleJumpToCloseConfirmed}
          onClose={async () => closeModal()}
        >
          <div>Are you sure you want to close this opportunity? This will set all phases to complete.</div>
        </StbModal>
      ),
    });
  };

  const handleJumpToCloseConfirmed = async () => {
    closeModal();
    await moveToStep(phases.length - 1);
  };

  useEffect(() => {
    let isMounted = true;

    const logFeedbackAndActivity = async () => {
      if (!oppData) return;

      // Log feedback
      const se_phase = getPhaseFromStep(currentStep);

      await postItem('db/feedback', {
        related_type: 'opportunity',
        related_id: oppData.id || oppData.Id,
        user: email,
        se_phase,
        json: JSON.stringify(feedback),
      });

      // Activity log message
      const phaseActivityMessages = {
        2: 'Opportunity Moved to Design phase.',
        3: 'Opportunity Moved to Validate phase.',
        4: 'Opportunity Moved to Close phase.',
      };

      // Turning off task creation for now
      // await handleWorkLogged(oppData, phaseActivityMessages[currentStep].summary, phaseActivityMessages[currentStep].description);
      await updateSENote(oppData, `${phaseActivityMessages[currentStep]}`);
    };

    if (logAfterOppDataReload && oppData?.se_phase === getPhaseFromStep(currentStep)) {
      logFeedbackAndActivity();

      if (isMounted) {
        setLogAfterOppDataReload(false);
        setFeedback(null);
      }
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppData, logAfterOppDataReload]);

  // Feedback submit handler, called only when moving into a new, unreached phase with feedback required
  const handleSubmitFeedback = async (feedback) => {
    if (!oppData) return;

    const transition = transitionPhase.split('_to_') as OpportunityPhase[];
    const toPhase = transition[1];
    const toStep = getStepFromPhase(toPhase);

    const updatedOpportunity = {
      id: oppData.id || oppData.Id,
      se_phase: toPhase,
      highest_se_phase: maxPhase(oppData.highest_se_phase, toPhase),
      updated_by: email,
      customer_id: customer?.Id,
      init_config: true,
    };

    try {
      await updateOpportunity(updatedOpportunity);

      setLogAfterOppDataReload(toStep > 1);
      setCurrentStep(toStep);
      setHighestStep((prev) => Math.max(toStep, prev));
      setFeedback(feedback);
    } catch (error) {
      console.error('Error updating opportunity phase:', error);
    }

    setModalOpen(false);
  };

  // Allow selecting any phase.
  // If selecting a future unreached phase, progress the opportunity.
  // If selecting a previous/reached phase, just navigate the UI.
  const handleStepSelection = async (stepNumber: number) => {
    await moveToStep(stepNumber);
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  if (!oppData || !oppData.id) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {(!isLoadingOppData && oppData) && <NavHistoryTracker title={oppData.Name} />}

      <div className="flex flex-col gap-6 max-w-full overflow-hidden overflow-y-auto">
        <div className="border-b border-gray-300 py-4 flex flex-row px-6 justify-between items-center ">
          <h2 className="my-0">{title}</h2>

          <div className="flex flex-row gap-8">
          <OpportunityEscalationRequestor />

            <div className="flex flex-row">
              <OppCustomerCard />

              <StbButton
                role="default"
                onClick={() => setCreateEventOpen(true)}
                className="hbr-button-group__button hbr-button-group__button--inner"
              >
                Create Event
              </StbButton>

              <SEWorkLogger />
              <TechnicalBlockerLogger />
            </div>
          </div>
        </div>

        <div className="border-b border-gray-300 px-6 pb-8 flex flex-row">
          <div>
            <StbButton
              disabled={currentStep === 0}
              onClick={() => handleBack(currentStep - 1)}
              size="small"
              icon={arrowLeft}
              className="hbr-button-group__button hbr-button-group__button--first"
            >
              Back
            </StbButton>

            <StbButton
              disabled={currentStep >= phases.length - 1}
              onClick={handleNext}
              size="small"
              icon={arrowRight}
              className="hbr-button-group__button hbr-button-group__button--last"
            >
              Next
            </StbButton>
          </div>

          {currentStep >= 0 && highestStep >= 0 && (
            <HbrStepper
              key={`opportunity-stepper-${oppData.id || oppData.Id}-${currentStep}`}
              steps={steps}
              activeStepId={String(currentStep)}
              onHbr-step-change={(e) =>
                handleStepSelection(
                  e.detail.step ? parseInt(e.detail.step.id) : currentStep
                )
              }
              cancelVisible={false}
              secondaryVisible={false}
              primaryVisible={false}
              changesSavedVisible={false}
              direction="row"
              className="relative mx-4 flex-grow"
              style={{ '--hbr-spacing-md': '0px' } as React.CSSProperties}
            />
          )}

          <StbButton
            disabled={currentStep >= phases.length - 1}
            onClick={handleJumpToCloseClicked}
            icon={check}
            size="small"
          >
            Technical Close
          </StbButton>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8 px-6">
          <div>
            {visibleComponent}
          </div>

          <div className="min-w-2xs max-w-lg">
            <OppDataCard />

            <OpportunityEscalationList />

            <OppProducts />

            <HbrDivider className="mt-4" />

            <OppProducts competitive />

            <HbrDivider className="mt-4" />

            <OpportunityWorkHistoryList />
          </div>

          {FEEDBACK_PHASE_GATING_ENABLED && (
            <OppPhaseTransitionModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              onCancel={handleCancel}
              onSubmitFeedback={handleSubmitFeedback}
              phase={transitionPhase}
            />
          )}
        </div>
      </div>

      <CreateEventLauncher
        mode="create"
        open={createEventOpen}
        onClose={() => setCreateEventOpen(false)}
        customerId={oppData.AccountId}
        opportunityId={oppData.id || oppData.Id}
      />
    </>
  );
};

export default OpportunityDetail;